import { useState } from 'react';
import { useRunQcContacts } from '../../../hooks/useRunQcContacts';
import styles from '../shared/QcPanel.module.css';

export interface RunUpdateButtonProps {
  programId: string;
  baciCode: string;
  seedUrl: string;
  organization?: string;
  onComplete?: () => void;
}

export function RunUpdateButton({
  programId,
  baciCode,
  seedUrl,
  organization,
  onComplete
}: RunUpdateButtonProps) {
  const [lastResult, setLastResult] = useState<string | null>(null);

  const [reportUrl, setReportUrl] = useState<string | null>(null);

  const runQcMutation = useRunQcContacts({
    onSuccess: (data) => {
      setLastResult(data.message || 'Verification complete!');
      setReportUrl(data.report_path || null);
      onComplete?.();
    },
    onError: (error) => {
      setLastResult(`Error: ${error.message}`);
    }
  });

  const handleClick = () => {
    if (!seedUrl) {
      setLastResult('Error: No seed URL available for this program');
      return;
    }

    setLastResult(null);
    setReportUrl(null);
    runQcMutation.mutate({
      program_id: programId,
      baci_code: baciCode,
      seed_url: seedUrl,
      organization
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', flex: 1 }}>
      <button
        onClick={handleClick}
        disabled={runQcMutation.isPending || !seedUrl}
        style={{
          padding: '0.75rem 1.5rem',
          backgroundColor: runQcMutation.isPending ? '#d1d5db' : '#8b5cf6',
          color: '#ffffff',
          border: 'none',
          borderRadius: '0.375rem',
          fontSize: '0.875rem',
          fontWeight: '600',
          cursor: runQcMutation.isPending || !seedUrl ? 'not-allowed' : 'pointer',
          opacity: runQcMutation.isPending || !seedUrl ? 0.6 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          width: 'fit-content'
        }}
      >
        {runQcMutation.isPending && <span className={styles.spinner} />}
        {runQcMutation.isPending ? 'Running BAMCheck...' : 'BAMCheck'}
      </button>

      {!seedUrl && (
        <div style={{ fontSize: '0.875rem', color: '#dc2626' }}>
          No seed URL available - cannot run verification
        </div>
      )}

      {lastResult && (
        <div
          style={{
            padding: '0.75rem',
            backgroundColor: runQcMutation.isError ? '#fef2f2' : '#f0fdf4',
            border: `1px solid ${runQcMutation.isError ? '#fca5a5' : '#86efac'}`,
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            color: runQcMutation.isError ? '#991b1b' : '#059669',
            maxWidth: '600px',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem'
          }}
        >
          <span>{lastResult}</span>
          {reportUrl && !runQcMutation.isError && (
            <a
              href={reportUrl.startsWith('http') ? reportUrl : `/${reportUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#059669',
                fontWeight: '600',
                textDecoration: 'underline'
              }}
            >
              View QC Report →
            </a>
          )}
        </div>
      )}

      {runQcMutation.isPending && (
        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
          The agent is verifying contacts against the website. This may take 30-60 seconds...
        </div>
      )}
    </div>
  );
}

export default RunUpdateButton;
